// Iter 391 (Renzo) — Historial de nóminas menores TEF a proveedores con re-descarga exacta.
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Download, History, Loader2, Landmark, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { descargarTxtB64 } from './NominaExcelDialog';

const CLP = (v) => Math.round(v || 0).toLocaleString('es-CL');
const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false }) : '');

export function NominasMenoresHistorialDialog({ open, onClose, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bajando, setBajando] = useState(null);
  const [procesando, setProcesando] = useState(null);

  const cargar = () => {
    setLoading(true);
    api.get('/tef/nomina-menor/historial', { params: { limit: 50 } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Error cargando historial'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) cargar(); }, [open, refreshKey]);

  const crearEgresos = async (n) => {
    setProcesando(n.id);
    try {
      const r = await api.post(`/tef/nomina-menor/historial/${n.id}/egresos-alegra`, {});
      if (r.data.creados) toast.success(`${r.data.creados} Comprobante(s) de Egreso creado(s) en Alegra`);
      if (r.data.errores) toast.warning(`${r.data.errores} documento(s) siguen sin egreso (ver detalle)`, { duration: 8000 });
      if (!r.data.creados && !r.data.errores) toast.info('Todas las líneas ya tienen egreso en Alegra');
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudieron crear los egresos', { duration: 10000 }); }
    finally { setProcesando(null); }
  };

  const anularLinea = async (n, idx, x) => {
    if (!window.confirm(`¿Anular en Alegra el egreso N°${x.egreso_alegra?.number || x.egreso_alegra?.payment_id} de ${x.nombre} (${x.asunto})? Solo si el TEF se descartó.`)) return;
    setProcesando(`${n.id}-${idx}`);
    try {
      await api.post(`/tef/nomina-menor/historial/${n.id}/egresos-alegra/${idx}/anular`);
      toast.success('Egreso anulado en Alegra');
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo anular', { duration: 10000 }); }
    finally { setProcesando(null); }
  };

  const descargar = async (n, parte) => {
    setBajando(`${n.id}-${parte}`);
    try {
      const r = await api.get(`/tef/nomina-menor/historial/${n.id}/archivo`, { params: { parte } });
      descargarTxtB64(r.data);
      toast.success(`${r.data.filename} descargado`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo descargar el archivo'); }
    finally { setBajando(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="dialog-historial-nominas-menores">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-slate-600" /> Historial de nóminas menores TEF · Proveedores</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>
        ) : items.length === 0 ? (
          <p className="rounded border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="historial-menores-empty">Aún no se han generado nóminas menores.</p>
        ) : (
          <div className="max-h-[60vh] overflow-auto rounded border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/80">
                <tr>
                  <th className="px-3 py-1.5 text-left">Fecha</th><th className="px-3 py-1.5 text-left">Detalle</th><th className="px-3 py-1.5 text-left">Motivo</th>
                  <th className="px-3 py-1.5 text-right">Transf.</th><th className="px-3 py-1.5 text-right">Total</th><th className="px-3 py-1.5 text-left">Generada por</th><th className="px-3 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {items.map((n) => (
                  <tr key={n.id} className="border-t align-top" data-testid={`historial-menor-fila-${n.id}`}>
                    <td className="whitespace-nowrap px-3 py-1.5">{fmtFecha(n.generada_at)} <span className="text-muted-foreground">{hora(n.generada_at)}</span></td>
                    <td className="px-3 py-1.5 max-w-[420px]">
                      {(n.incluidas || []).map((x, i) => {
                        const e = x.egreso_alegra;
                        return (
                          <div key={i} className="flex items-center gap-1 truncate" title={`${x.nombre} · ${x.asunto}${e?.error ? ` · ${e.error}` : ''}`} data-testid={`menor-linea-${n.id}-${i}`}>
                            <span className="truncate">{x.asunto} · {x.nombre} · ${CLP(x.monto)}</span>
                            {e?.estado === 'creado' && (
                              <>
                                <span className="shrink-0 text-sky-700"><Landmark className="inline h-3 w-3" /> N°{e.number || e.payment_id}</span>
                                <button type="button" className="shrink-0 text-red-600 hover:underline" onClick={() => anularLinea(n, i, x)} disabled={procesando === `${n.id}-${i}`} data-testid={`btn-anular-menor-${n.id}-${i}`} title="Anular egreso en Alegra"><Ban className="inline h-3 w-3" /></button>
                              </>
                            )}
                            {e?.estado === 'anulado' && <span className="shrink-0 text-slate-500 line-through">N°{e.number || e.payment_id}</span>}
                            {e?.estado === 'error' && <span className="shrink-0 text-red-700">⚠ sin egreso</span>}
                            {!e && <span className="shrink-0 text-muted-foreground">· sin egreso</span>}
                          </div>
                        );
                      })}
                    </td>
                    <td className="max-w-[160px] truncate px-3 py-1.5" title={n.motivo}>{n.motivo}</td>
                    <td className="px-3 py-1.5 text-right">{n.num_transacciones}{n.omitidas?.length ? <Badge variant="outline" className="ml-1 text-[9px] text-amber-700">{n.omitidas.length} omit.</Badge> : null}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-medium">${CLP(n.monto_total)}</td>
                    <td className="max-w-[140px] truncate px-3 py-1.5 text-muted-foreground" title={n.generada_por}>{(n.generada_por || '').split('@')[0]}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      {(n.incluidas || []).some((x) => (x.egreso_alegra?.estado || 'none') !== 'creado') && (
                        <Button size="sm" variant="outline" className="h-7 gap-1 px-2 border-sky-300 text-sky-700 hover:bg-sky-50" onClick={() => crearEgresos(n)} disabled={procesando === n.id} data-testid={`btn-crear-egresos-menor-${n.id}`} title="Crear en Alegra los egresos de las líneas que aún no lo tienen">
                          {procesando === n.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Landmark className="h-3.5 w-3.5" />} Alegra
                        </Button>
                      )}
                      {(n.archivos || []).map((a, i) => (
                        <Button key={i} size="sm" variant="outline" className="h-7 gap-1 px-2 ml-1" onClick={() => descargar(n, i + 1)} disabled={bajando === `${n.id}-${i + 1}`} data-testid={`btn-redescargar-menor-${n.id}-${i + 1}`} title={a.filename}>
                          {bajando === `${n.id}-${i + 1}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} TEF{n.archivos.length > 1 ? ` ${i + 1}` : ''}
                        </Button>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
